import React, { ReactNode, useEffect, useState } from 'react';
import { Layout, Menu, Button, MenuProps, Tooltip, Modal } from 'antd';
import { usePathname, useRouter } from 'next/navigation';
import AppHeader from '../AppHeader/AppHeader';
import { Footer } from 'antd/es/layout/layout';
import serviceUtil from 'src/Utils/serviceUtil/serviceUtil';
import CustomModal from '@/components/Atoms/CustomModal';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { resetFormState } from 'src/GlobalRedux/addCompanySlice/addCompanySlice';
import { useGlobalContext } from 'src/contexts/GlobalContext';
import { ENERGY_MENU_ITEM, ENERGY_REPORT_URL, MACHINE_ACTIVITY_REPORT_URL } from 'src/Utils/menuUtil';

const { Header, Content, Sider } = Layout;

interface SubMenu {
  Menu_Label: string;
  isActive: boolean;
  Menu_URL: string;
  Menu_Icon?: string;
}

interface MenuItem {
  Menu_Label: string;
  isActive: boolean;
  Menu_URL: string;
  Menu_Icon: string;
  subMenu?: SubMenu[] | undefined;
}

type MenuItemType = Required<MenuProps>['items'][number];

export const getIconByName = (iconName: string, isSelected: boolean, isActive: boolean, isHovered: boolean): React.ReactNode => {
  const disabledIconStyle = { style: { color: '#737373' } };

  const getIconPath = (iconName: string, state: string) => (state ? `/${iconName}-${state}.svg` : `/${iconName}.svg`);

  const iconPath = !isActive
    ? getIconPath(iconName, 'disabled')
    : isHovered && !isSelected && isActive
      ? getIconPath(iconName, 'hovered')
      : isSelected && isActive
        ? getIconPath(iconName, 'selected')
        : getIconPath(iconName, '');

  return iconName ? (
    <img
      className="menu-icon"
      src={iconPath}
      alt={iconName}
      {...(isActive ? disabledIconStyle : {})}
    />
  ) : (
    <></>
  );
};

function getItem(label: React.ReactNode, key: React.Key, icon: React.ReactNode, children?: MenuItemType[], isActive?: boolean): MenuItemType {
  return {
    key,
    icon,
    children,
    label,
    className: isActive ? '' : 'disabled-item',
  } as MenuItemType;
}

// Function to create Tooltip-wrapped Menu label
const createTooltipLabel = (label: string) => (
  <Tooltip
    title={label}
    className="text:xs 3xl:text-sm"
  >
    <span>{label} </span>
  </Tooltip>
);

const mapMenuItems = (
  menu: MenuItem[],
  openKeys: string[],
  setOpenKeys: (keys: string[]) => void,
  hoveredItem: string,
  setHoveredItem: (key: string) => void,
  pathName: string,
  setSelectedSubMenu: (url: string) => void
): MenuItemType[] => {
  const isAnySubmenuSelected = (subMenu?: SubMenu[]) => {
    return subMenu?.some((subItem) => pathName === subItem.Menu_URL) || false;
  };

  const mappedMenu = menu?.map((item) => {
    const isSelected = pathName === item.Menu_URL || isAnySubmenuSelected(item.subMenu);
    const hasSubMenu = item.subMenu && item.subMenu.length > 0;
    const icon = getIconByName(item.Menu_Icon, isSelected, item.isActive, hoveredItem === item.Menu_URL);

    return getItem(
      item.Menu_Label,
      item.Menu_URL,
      icon,
      hasSubMenu ? mapMenuItems(item?.subMenu, openKeys, setOpenKeys, hoveredItem, setHoveredItem, pathName, setSelectedSubMenu) : undefined,
      item.isActive
    );
  });
  return mappedMenu;
};

const MainLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const pathName = usePathname();
  const router = useRouter();
  const dispatch = useDispatch();
  const [collapsed, setCollapsed] = useState(false);
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [hoveredItem, setHoveredItem] = useState<string>('');
  const [menu, setMenu] = useState<MenuItem[]>([]);
  const [selectedSubMenu, setSelectedSubMenu] = useState<string>(pathName);
  const [isEnergyModalVisible, setIsEnergyModalVisible] = useState(false);
  const { t, i18n } = useTranslation();

  const { isUnsavedChangesModalVisible, setIsUnsavedChangesModalVisible, newUrl, setNewUrl, onModalConfirm, setOnModalConfirm } = useGlobalContext();

  const updateTranslatedMenu = async (menu: MenuItem[]) => {
    setMenu(menu);
  };
  useEffect(() => {
    const storedMenu = localStorage.getItem('userMenu');
    const parsedMenu = storedMenu ? JSON.parse(storedMenu) : [];
    const resolvedMenu = Array.isArray(parsedMenu) ? parsedMenu : [];
    setMenu(resolvedMenu);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const isDesktop = window.innerWidth >= 1024;
      setCollapsed(!isDesktop);
    };

    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!menu || menu.length === 0) {
      return;
    }

    const findOpenKey = (menuItems: MenuItem[], currentPath: string): string | null => {
      for (const item of menuItems) {
        if (item.subMenu) {
          for (const subItem of item.subMenu) {
            if (subItem.Menu_URL === currentPath) {
              return item.Menu_URL;
            }
          }
        }
      }
      return null;
    };

    const openKey = findOpenKey(menu, pathName);
    if (openKey && !collapsed) {
      setOpenKeys([openKey]);
    } else {
      setOpenKeys([])
    }
  }, [collapsed, menu, pathName]);

  const onOpenChange = (keys: string[]) => {
    if (!collapsed) {
      setSelectedSubMenu(pathName);
    }
    const latestOpenKey = keys.find((key) => !openKeys.includes(key));
    if (latestOpenKey) {
      setOpenKeys([latestOpenKey]);
    } else {
      setOpenKeys([]);
    }
  };

  const handleMenuClick = (itemKey: string, hasSubMenu: boolean) => {
    if (collapsed && hasSubMenu) {
      const isCurrentlyOpen = openKeys.includes(itemKey);
      setOpenKeys(isCurrentlyOpen ? [] : [itemKey]);
    } else if (!hasSubMenu) {
      if (itemKey === ENERGY_MENU_ITEM.Menu_URL) {
        setIsEnergyModalVisible(true);
        return;
      }
      const blockNavigation = localStorage.getItem('blockNavigation');
      if (blockNavigation && pathName !== itemKey) {
        setNewUrl(itemKey);
        setIsUnsavedChangesModalVisible(true);
      } else {
        setSelectedSubMenu(itemKey);
        router.push(itemKey);
      }
    }
  };

  const getIcon = (icon: React.ReactNode, isActive: boolean, isSelected: boolean, isHovered: boolean) => {
    const IconComponent = isSelected || isHovered ? React.cloneElement(icon as React.ReactElement) : icon;

    return isActive
      ? IconComponent
      : React.cloneElement(icon as React.ReactElement, {
        style: { color: '#737373' },
      });
  };

  const generateMenuItems = (items: MenuItemType[]): MenuProps['items'] => {
    return items?.map((item) => {
      const isSelected = pathName === item?.key;
      const hasSubMenu = item?.children && item?.children?.length > 0;
      const isSubmenuExpanded = openKeys.includes(item?.key as string);
      const icon =
        collapsed && !isSubmenuExpanded ? (
          <Tooltip
            title={item?.label}
            placement="right"
            className="3xl:text-sm"
          >
            {getIcon(item?.icon, !item?.className?.includes('disabled-item'), isSelected, hoveredItem === item?.key)}
          </Tooltip>
        ) : (
          getIcon(item?.icon, !item?.className?.includes('disabled-item'), isSelected, hoveredItem === item?.key)
        );

      return {
        key: item?.key,
        icon,
        label: item?.label,
        disabled: item?.className?.includes('disabled-item'),
        children: item?.children ? generateMenuItems(item?.children) : undefined,
        onClick: () => handleMenuClick(item?.key as string, hasSubMenu),
        onMouseEnter: () => setHoveredItem(item?.key as string),
        onMouseLeave: () => setHoveredItem(''),
        className: openKeys.includes(item?.key as string) ? 'menu-item-open' : 'menu-item-close',
      } as MenuItemType;
    });
  };

  useEffect(() => {
    setSelectedSubMenu(pathName);
  }, [pathName]);

  const closeModal = () => {
    setIsUnsavedChangesModalVisible(false);
  };

  const closeEnergyModal = () => {
    setIsEnergyModalVisible(false);
  };

  const openReportInNewTab = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
    closeEnergyModal();
  };

  const handleOk = () => {
    onModalConfirm?.();
    setOnModalConfirm(null);
    if (newUrl) {
      router.push(newUrl);
      setNewUrl('');
    }
    setIsUnsavedChangesModalVisible(false);
    localStorage.removeItem('blockNavigation');
    if (newUrl?.includes('company')) dispatch(resetFormState());
  };

  let menuItems = mapMenuItems(menu, openKeys, setOpenKeys, hoveredItem, setHoveredItem, selectedSubMenu, setSelectedSubMenu);
  menuItems = generateMenuItems(menuItems);

  const CustomExpandIcon = (props): React.ReactNode => {
    const expandedMenu = selectedSubMenu
      .split('/')
      .map((p) => '/' + p)
      .includes(props.eventKey);
    const isInopenKeys = openKeys.includes(props.eventKey);
    return (
      <>
        {collapsed && !isInopenKeys ? (
          ''
        ) : (
          <span
            className={`${props.className} ${collapsed && isInopenKeys && 'mt-2 3xl:mt-4'}`}
            style={{
              marginLeft: 'auto',
              transform: props.isOpen ? 'rotate(0deg)' : 'rotate(180deg)',
              transition: '0.3s',
              position: 'absolute',
              right: '10px',
            }}
          >
            <img
              height={24}
              width={24}
              src={expandedMenu ? '/chevron-up.svg' : '/chevron-up-grey.svg'}
              alt="expand icon"
            />
          </span>
        )}
      </>
    );
  };

  return (
    <Layout style={{ fontSize: '16px', minHeight: '100vh' }}>
      <Sider
        collapsible
        collapsed={collapsed}
        trigger={null}
        style={{
          height: '100vh',
          position: 'fixed',
          left: 0,
          display: 'flex',
          flexDirection: 'column',
          zIndex: 100,
        }}
        data-testid="antd-sider"
      >
        <div className="flex justify-center items-center">
          {collapsed ? (
            <img
              src="/de-karb-logo-half.svg"
              alt="logo-icon"
              className="m-4"
            />
          ) : (
            <img
              src="/de-karb-logo-full.svg"
              alt="logo-icon"
              className="m-4"
            />
          )}
        </div>

        <div
          style={{
            flex: 1,
          }}
        >
          <Menu
            data-testid={`${selectedSubMenu}`}
            selectedKeys={[selectedSubMenu]}
            mode="inline"
            openKeys={openKeys}
            onOpenChange={onOpenChange}
            items={menuItems}
            className={`mt-4 w-[261px] ${collapsed ? 'menu-collapsed' : ''}`}
            style={{ fontSize: '16px' }}
            triggerSubMenuAction="click"
            motion={{ motionAppear: false, motionEnter: false, motionLeave: false }}
            expandIcon={(props) => <CustomExpandIcon {...props} />}
          />
        </div>

        <div
          style={{
            position: 'absolute',
            bottom: 0,
            width: '100%',
            padding: '12px',
            textAlign: 'center',
            fontSize: '16px',
            color: '#A3A3A3',
          }}
        >
          <a
            data-testid="Privacy_Settings-link"
            href="#"
            onClick={() => {
              if (window.UC_UI) {
                window.UC_UI.showSecondLayer();
              } else {
                console.error('Usercentrics UC_UI is not available.');
              }
            }}
          >
            Privacy Settings
          </a>
        </div>
      </Sider>

      <Layout className={`${collapsed ? 'md:ml-[50px] lg:ml-[80px]' : 'md:ml-[200px] lg:ml-[260px] h-auto min-h-full'}`}>
        <div
          className={`${collapsed
              ? 'fixed top-4 bg-transparent z-[101] md:left-[36px] lg:left-[62px]'
              : 'fixed top-4 bg-transparent z-[101] md:left-[182px] lg:left-[242px]'
            }`}
        >
          <Button
            data-testid="Menu_close"
            type="text"
            icon={
              collapsed ? (
                <img
                  src="/circle_open.svg"
                  alt="sider-open"
                />
              ) : (
                <img
                  src="/circle_close.svg"
                  alt="sider-close"
                />
              )
            }
            onClick={() => {
              setCollapsed((previouslyCollapsed) => {
                return !previouslyCollapsed;
              });
            }}
            style={{ fontSize: '16px', border: 'none' }}
          />
        </div>
        <AppHeader
          selectedSubMenu={selectedSubMenu}
          setSelectedSubMenu={setSelectedSubMenu}
          updateTranslatedMenu={updateTranslatedMenu}
        />
        <Content style={{ overflow: 'hidden', height: 'calc(100vh - 70px)' }}>{children}</Content>
        {/* <br />
        <Footer
          style={{
            textAlign: 'center',
            background: '#f5f5f5',
            padding: '0px'
          }}
        >
        </Footer> */}
        <CustomModal
          isVisible={isUnsavedChangesModalVisible}
          onCancel={closeModal}
          onOk={handleOk}
          title={''}
          description={t('Are you sure you want to navigate to other page without saving your changes?')}
          iconType={'Warning'}
          primaryButtonText={'Yes'}
          secondaryButtonText="No"
          zindex={1050}
        />
        <Modal
          data-testid="energy-report-modal"
          open={isEnergyModalVisible}
          onCancel={closeEnergyModal}
          footer={null}
          centered
          title={t('Reports')}
        >
          <div className="flex flex-row justify-center gap-4 py-2">
            <Button
              data-testid="Energy-report-button"
              type="primary"
              onClick={() => openReportInNewTab(ENERGY_REPORT_URL)}
            >
              {t('Energy')}
            </Button>
            <Button
              data-testid="Machine-Activity-report-button"
              type="primary"
              onClick={() => openReportInNewTab(MACHINE_ACTIVITY_REPORT_URL)}
            >
              {t('View Machine Activity HAAN')}
            </Button>
          </div>
        </Modal>
      </Layout>
    </Layout>
  );
};

export default MainLayout;
